// ─────────────────────────────────────────────────────────────────────────────
// selectBranch — change branch on the SERVER first, then on screen.
//
// Ported from the agent app (agent_web_app_print_routing, utils/selectBranch.js).
//
// WHY. The cart is READ for the restaurant the SERVER has stored for this
// customer (backend `list-cart` joins menu items on GetSelectedRestaurant(user)),
// while the ORDER is posted with the website's `selectedStore`. If the two ever
// disagree:
//   • items the customer adds are saved but never read back (the cart looks empty),
//   • or an order is posted whose header names branch B while its items belong
//     to branch A — and the backend does NOT check that (it looks items up by id
//     and writes the names and prices the client sent), so it is accepted.
// The website used to switch on screen at once and save to the server later, in
// the background, ignoring errors. Now the screen moves only once the server has.
//
// Touches no React state: the caller (the header's branch dropdown) does that.
// ─────────────────────────────────────────────────────────────────────────────
import ApiConfig from "../api/ApiConfig";
import { PatchMultipartWithAuthCallWithErrorResponse } from "../api/ApiServices";

// One switch in flight at a time: a double-tap would otherwise send two saves
// that can land in either order.
let inFlight = false;

const TIMED_OUT = Symbol("select-branch-timeout");

// PATCH /api/restaurant/select has a path that sends NO response at all
// (restaurant.controller.js logs and returns), so a bare await could leave the
// dropdown waiting forever. Race it.
function withTimeout(promise, ms) {
  if (!ms) return promise;
  let tid;
  return Promise.race([
    promise.finally(() => clearTimeout(tid)),
    new Promise((resolve) => {
      tid = setTimeout(() => resolve(TIMED_OUT), ms);
    }),
  ]);
}

/**
 * Save the customer's selected restaurant on the server.
 *
 * @param {string|number} restaurantId  the branch to move to
 * @param {object} ctx  { userId, selectedStore, timeoutMs }
 * @returns {Promise<{success:true, changed:boolean} |
 *                   {success:false, reason:"branch"|"timeout"|"busy"|"signed_out"}>}
 *          `changed:false` means it already was the selected branch: nothing was sent.
 *          `signed_out` means there is no customer to save it for; the caller may
 *          switch on screen only (a signed-out visitor has no server cart).
 */
export async function selectBranch(
  restaurantId,
  { userId, selectedStore, timeoutMs = 8000 } = {}
) {
  if (restaurantId == null || restaurantId === "") {
    return { success: false, reason: "branch" };
  }
  if (String(restaurantId) === String(selectedStore)) {
    return { success: true, changed: false };
  }
  if (userId == null || userId === "") return { success: false, reason: "signed_out" };
  if (inFlight) return { success: false, reason: "busy" };

  inFlight = true;
  try {
    const res = await withTimeout(
      PatchMultipartWithAuthCallWithErrorResponse(
        ApiConfig.PATCH_RESTAURANT_SELECT,
        JSON.stringify({ user_id: userId, restaurant_id: restaurantId })
      ),
      timeoutMs
    );
    if (res === TIMED_OUT) return { success: false, reason: "timeout" };
    // ApiServices resolves `{ error: !resp.ok }` rather than rejecting on an HTTP error.
    if (res?.error) return { success: false, reason: "branch" };
    return { success: true, changed: true };
  } catch {
    return { success: false, reason: "branch" };
  } finally {
    inFlight = false;
  }
}
